/**
 * @file backup.service.ts
 * @description Angular service for managing database backups, Excel exports, and rollback restorations.
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, map, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BackupStatusResponse,
  BackupSummary,
  RollbackResponse,
} from '../interfaces/backup.interfaces';

/**
 * Impact summary returned by POST /backup/dehydrate/analyze.
 * The analysis is read-only: it reports what WOULD be purged for a given
 * cutoff date, without touching any records.
 */
export interface DehydrationCollectionImpact {
  key: string;
  name: string;
  matchedCount: number;
  collectionKey?: string;
  collectionName?: string;
  purgedCount?: number;
}

export interface DehydrationAnalysis {
  cutoffDate: string;
  cutoffDateKey?: string;
  totalMatched: number;
  totalDocuments?: number;
  totalBytesFreed?: number;
  purgedActivitiesCount?: number;
  retainedActivitiesCount?: number;
  collections: DehydrationCollectionImpact[];
  [key: string]: unknown;
}

export interface DehydrationDiscoveryResult {
  maxEligibleCutoffDate: string;
  availableCutoffs: DehydrationAnalysis[];
}

export interface DehydrationAnalysisResponse {
  status?: string;
  message?: string;
  data?: DehydrationAnalysis & Partial<DehydrationDiscoveryResult>;
  // Some backends return the fields at the top level; keep both shapes usable.
  cutoffDate?: string;
  totalMatched?: number;
  totalDocuments?: number;
  collections?: DehydrationCollectionImpact[];
  maxEligibleCutoffDate?: string;
  availableCutoffs?: DehydrationAnalysis[];
}

/**
 * Result of a dehydration download: the ZIP blob plus the one-time purge
 * token the backend issues so a later purge can be authorized.
 */
export interface DehydrationDownloadResult {
  blob: Blob;
  purgeToken: string | null;
  manifest?: any | null;
}

export interface DehydrationPurgeResponse {
  status?: string;
  message?: string;
  purgedCount?: number;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class BackupService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  /**
   * Fetches database status, collection counts and document totals.
   */
  getStatus(): Observable<BackupSummary> {
    return this.http
      .get<BackupStatusResponse>(`${this.apiUrl}/backup/status`)
      .pipe(map((res) => res.data));
  }

  /**
   * Downloads the complete database as a compressed JSON ZIP archive.
   */
  downloadJsonBackup(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/backup/download/json`, {
      responseType: 'blob',
    });
  }

  /**
   * Downloads the complete database as a styled Microsoft Excel (.xlsx) workbook.
   */
  downloadExcelBackup(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/backup/download/excel`, {
      responseType: 'blob',
    });
  }

  /**
   * Uploads a backup ZIP file for dry-run inspection (confirm=false)
   * or actual rollback restoration (confirm=true).
   */
  restoreBackup(file: File, confirm: boolean = false): Observable<RollbackResponse> {
    const formData = new FormData();
    formData.append('backup', file);
    formData.append('confirm', confirm ? 'true' : 'false');

    return this.http.post<RollbackResponse>(`${this.apiUrl}/backup/restore`, formData);
  }

  /* ========================================================================
     DESHIDRATACIÓN / CORTE DE REGISTROS (Fase D.2 - RA4-1)
     Flujo guiado en 4 pasos: analizar sistema (lectura) -> elegir fecha
     disponible -> descargar corte (emite token) -> purgar (requiere el token).
     El análisis NUNCA ejecuta borrado ni se dispara automáticamente.
     ======================================================================== */

  private normalizeAnalysisItem(raw: Record<string, unknown>): DehydrationAnalysis {
    const cutoffDate = String(raw?.['cutoffDate'] ?? '');
    const cutoffDateKey = String(raw?.['cutoffDateKey'] ?? cutoffDate.slice(0, 10));
    const totalMatched = Number(raw?.['totalMatched'] ?? raw?.['totalDocuments'] ?? 0);
    const rawCols = Array.isArray(raw?.['collections']) ? raw['collections'] : [];

    const collections: DehydrationCollectionImpact[] = rawCols.map(
      (col: Record<string, unknown>) => ({
        key: String(col?.['key'] ?? col?.['collectionKey'] ?? ''),
        name: String(col?.['name'] ?? col?.['collectionName'] ?? ''),
        matchedCount: Number(col?.['matchedCount'] ?? col?.['purgedCount'] ?? 0),
        collectionKey: col?.['collectionKey'] as string | undefined,
        collectionName: col?.['collectionName'] as string | undefined,
        purgedCount: col?.['purgedCount'] as number | undefined,
      }),
    );

    return {
      ...raw,
      cutoffDate,
      cutoffDateKey,
      totalMatched,
      totalDocuments: Number(raw?.['totalDocuments'] ?? totalMatched),
      totalBytesFreed: Number(raw?.['totalBytesFreed'] ?? 0),
      purgedActivitiesCount: Number(raw?.['purgedActivitiesCount'] ?? 0),
      retainedActivitiesCount: Number(raw?.['retainedActivitiesCount'] ?? 0),
      collections,
    };
  }

  /**
   * Escanea la base de datos bajo demanda para descubrir fechas de corte
   * disponibles con su resumen de impacto precalculado.
   * POST /backup/dehydrate/analyze {} -> { maxEligibleCutoffDate, availableCutoffs }
   */
  discoverDehydrationCutoffs(): Observable<DehydrationDiscoveryResult> {
    return this.http
      .post<DehydrationAnalysisResponse>(`${this.apiUrl}/backup/dehydrate/analyze`, {})
      .pipe(
        map((res) => {
          const payload = (res?.data ?? res ?? {}) as Record<string, unknown>;
          const rawList = Array.isArray(payload['availableCutoffs'])
            ? (payload['availableCutoffs'] as Record<string, unknown>[])
            : [];
          return {
            maxEligibleCutoffDate: String(payload['maxEligibleCutoffDate'] ?? ''),
            availableCutoffs: rawList.map((item) => this.normalizeAnalysisItem(item)),
          };
        }),
      );
  }

  /**
   * Analiza el impacto de un corte por fecha puntual SIN ejecutar ningún borrado.
   * POST /backup/dehydrate/analyze { cutoffDate } -> resumen de impacto.
   */
  analyzeDehydration(cutoffDate: string): Observable<DehydrationAnalysis> {
    return this.http
      .post<DehydrationAnalysisResponse>(`${this.apiUrl}/backup/dehydrate/analyze`, {
        cutoffDate,
      })
      .pipe(
        map((res) => {
          const payload = (res?.data ?? res ?? {}) as Record<string, unknown>;
          return this.normalizeAnalysisItem(payload);
        }),
      );
  }

  /**
   * Descarga el ZIP del corte y captura el `purgeToken` emitido por el backend,
   * además del manifiesto estructurado expuesto en cabeceras para generar el .txt legible.
   * POST /backup/dehydrate/download { cutoffDate } -> ZIP + purgeToken + manifest.
   */
  downloadDehydration(cutoffDate: string): Observable<DehydrationDownloadResult> {
    return this.http
      .post(`${this.apiUrl}/backup/dehydrate/download`, { cutoffDate }, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError(async (err) => {
          if (err?.error instanceof Blob) {
            try {
              const text = await err.error.text();
              const parsed = JSON.parse(text);
              if (parsed?.message) {
                err.error.message = parsed.message;
              }
            } catch (_) {
              // noop
            }
          }
          throw err;
        }),
        map((response: HttpResponse<Blob>) => {
          const purgeToken =
            response.headers.get('X-Purge-Token') ||
            response.headers.get('x-purge-token');

          let manifest: any = null;
          const manifestRaw =
            response.headers.get('X-Dehydration-Manifest') ||
            response.headers.get('x-dehydration-manifest');
          if (manifestRaw) {
            try {
              const binaryStr = atob(manifestRaw.replace(/-/g, '+').replace(/_/g, '/'));
              manifest = JSON.parse(binaryStr);
            } catch (_) {
              manifest = null;
            }
          }

          return {
            blob: response.body as Blob,
            purgeToken: purgeToken ?? null,
            manifest,
          };
        }),
      );
  }

  /**
   * Genera el contenido en formato Markdown para el archivo complementario .txt de lectura humana.
   */
  buildDehydrationMarkdownSummary(manifestOrAnalysis: any, dateSlug?: string): string {
    const now = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
    const cutoff = manifestOrAnalysis?.cutoffDate
      ? String(manifestOrAnalysis.cutoffDate).slice(0, 10)
      : (dateSlug || 'N/A');
    const totalDocs =
      manifestOrAnalysis?.metrics?.totalDocuments ??
      manifestOrAnalysis?.totalMatched ??
      manifestOrAnalysis?.totalDocuments ?? 0;
    const bytesFreed =
      manifestOrAnalysis?.metrics?.totalBytesFreed ??
      manifestOrAnalysis?.totalBytesFreed ?? 0;
    const sha = manifestOrAnalysis?.metrics?.packageSha256 || 'n/a';
    const kb = (bytesFreed / 1024).toFixed(2);

    const lines = [
      '# ============================================================================== #',
      '# ABAQ QUERÉTARO — REPORTE DE CORTE HISTÓRICO Y DESHIDRATACIÓN DE DATOS         #',
      '# ============================================================================== #',
      '',
      `- Fecha de generación: ${now}`,
      `- Fecha límite de corte: ${cutoff} (colaboradores inactivos y convocatorias hasta esta fecha)`,
      `- Total de documentos archivados: ${totalDocs}`,
      `- Espacio liberado estimado: ${kb} KB (${bytesFreed} bytes)`,
      `- Suma criptográfica SHA-256 del paquete: ${sha}`,
      '',
      '> NOTA INFORMATIVA:',
      '> Este archivo ha sido generado exclusivamente para consulta, lectura y auditoría humana.',
      '> Para rehidratar o restablecer estos registros en la plataforma, utiliza únicamente',
      '> el archivo .zip complementario en la sección "Rehidratación de Corte Histórico".',
      '> Los procesos automáticos y scripts del sistema ignoran por completo este archivo .txt.',
      '',
      '----------------------------------------------------------------------------------',
      'DESGLOSE POR COLECCIÓN Y MODELO INSTITUCIONAL',
      '----------------------------------------------------------------------------------',
      '| Colección | Modelo Mongoose | Documentos | Hash SHA-256 (integridad) |',
      '| :--- | :--- | :--- | :--- |',
    ];

    const colls = manifestOrAnalysis?.collections;
    if (colls && typeof colls === 'object') {
      if (Array.isArray(colls)) {
        for (const c of colls) {
          const count = c.matchedCount ?? c.purgedCount ?? 0;
          lines.push(`| ${c.name || c.key} | ${c.key} | ${count} | verificado en manifest.json |`);
        }
      } else {
        for (const [key, val] of Object.entries<any>(colls)) {
          const name = val.modelName || key;
          const count = val.documentCount ?? 0;
          const hash = val.sha256 ? `${val.sha256.slice(0, 16)}...` : 'n/a';
          lines.push(`| ${name} | ${key} | ${count} | ${hash} |`);
        }
      }
    }

    lines.push('');
    lines.push('----------------------------------------------------------------------------------');
    lines.push('INSTRUCCIONES DE REHIDRATACIÓN');
    lines.push('----------------------------------------------------------------------------------');
    lines.push('1. Conserva este archivo de texto junto con el archivo .zip en almacenamiento seguro.');
    lines.push('2. Para reingresar estos datos a la base de datos de ABAQ:');
    lines.push('   a. Ingresa como Administrador a la plataforma en la sección /backup.');
    lines.push('   b. Dirígete a la sección "Rehidratación de Corte Histórico".');
    lines.push('   c. Sube el archivo .zip y pulsa "Validar Paquete de Corte (Dry-Run)".');
    lines.push('   d. Verifica que no existan colisiones de correo y confirma la restauración.');
    lines.push('');
    lines.push('==================================================================================');

    return lines.join('\n');
  }

  /**
   * Sube un archivo ZIP de deshidratación para inspección preliminar dry-run (confirm=false)
   * o rehidratación definitiva (confirm=true).
   */
  restoreDehydrationPackage(file: File, confirm: boolean = false): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('confirm', confirm ? 'true' : 'false');

    return this.http.post<any>(`${this.apiUrl}/backup/dehydrate/restore`, formData);
  }

  /**
   * Dispara la descarga de un archivo de texto plano / Markdown en el navegador.
   */
  triggerDownloadText(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    this.triggerDownload(blob, filename);
  }

  /**
   * Ejecuta el borrado definitivo de los registros del corte.
   * POST /backup/dehydrate/purge { cutoffDate, purgeToken } -> ejecuta borrado.
   */
  purgeDehydration(
    cutoffDate: string,
    purgeToken: string,
  ): Observable<DehydrationPurgeResponse> {
    return this.http.post<DehydrationPurgeResponse>(
      `${this.apiUrl}/backup/dehydrate/purge`,
      { cutoffDate, purgeToken },
    );
  }

  /**
   * Utility to trigger a local file download in the browser from a Blob.
   */
  triggerDownload(blob: Blob, fallbackFilename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fallbackFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
